const format = "pirdy.web-workspace.encrypted.v1";
const algorithm = "AES-GCM-256";
const mediaType = "application/vnd.pirdy.workspace-backup+json";
const localWorkspaceDatabaseName = "pirdy-web-local-workspaces-v1";
const localWorkspaceStoreName = "encrypted-workspaces";
const localWorkspaceRecordVersion = 1;
const mebibyte = 1024 * 1024;
function customerSafeBackupError(message) {
  const error = new Error(message);
  error.customerSafe = true;
  return error;
}
const defaultLimits = Object.freeze({
  // One current maximum-size Cloud media artifact plus its base64/encryption
  // overhead fits these bounds. Aggregate decoded bytes remain bounded so a
  // malicious file cannot make the browser allocate without a ceiling.
  maximumEncryptedFileBytes: 1024 * mebibyte,
  maximumCiphertextBytes: 768 * mebibyte,
  maximumPlaintextBytes: 768 * mebibyte - 16,
  maximumAttachmentCount: 4096,
  maximumAttachmentBytes: 512 * mebibyte,
  maximumDecodedAttachmentBytes: 512 * mebibyte,
});
const encoder = new TextEncoder();
const decoder = new TextDecoder("utf-8", { fatal: true });
const base64UrlAlphabet = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-_";

function cryptoApi(value = globalThis.crypto) {
  if (!value?.subtle || typeof value.getRandomValues !== "function")
    throw new Error("This browser cannot create or restore a secure Pirdy backup. Use a current browser with Web Crypto support.");
  return value;
}

function portableAccountId(value, missingMessage = "Sign in to the Pirdy account that owns this backup before continuing.") {
  const accountId = typeof value === "string" ? value.trim() : "";
  if (!accountId) throw new Error(missingMessage);
  if (accountId.length > 64 || /[\u0000-\u001f\u007f]/u.test(accountId))
    throw new Error("The portable Pirdy account identity is invalid.");
  return accountId;
}

function plainObject(value) {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

function exactKeys(value, names) {
  if (!plainObject(value)) return false;
  const keys = Object.keys(value).sort();
  const expected = [...names].sort();
  return keys.length === expected.length && keys.every((key, index) => key === expected[index]);
}

function operationLimits(overrides) {
  if (overrides === undefined) return defaultLimits;
  if (!plainObject(overrides)) throw new Error("The Pirdy backup safety limits are invalid.");
  const limits = { ...defaultLimits };
  for (const [name, value] of Object.entries(overrides)) {
    if (!Object.hasOwn(defaultLimits, name) || !Number.isSafeInteger(value) || value < 1 ||
        value > defaultLimits[name])
      throw new Error("The Pirdy backup safety limits are invalid.");
    limits[name] = value;
  }
  return limits;
}

function base64Url(bytes) {
  let binary = "";
  for (let index = 0; index < bytes.length; index += 0x8000)
    binary += String.fromCharCode(...bytes.subarray(index, index + 0x8000));
  return btoa(binary).replaceAll("+", "-").replaceAll("/", "_").replace(/=+$/u, "");
}

function base64UrlDecodedLength(value, message, options = {}) {
  const maximumBytes = options.maximumBytes ?? Number.MAX_SAFE_INTEGER;
  const limitMessage = options.limitMessage ?? message;
  if (typeof value !== "string" || value.length === 0) throw new Error(message);
  // Check the encoded character count before atob allocates its decoded binary
  // string. The decoded-length check below is repeated for clarity and safety.
  if (value.length > Math.ceil(maximumBytes * 4 / 3)) throw new Error(limitMessage);
  if (value.length % 4 === 1 || !/^[A-Za-z0-9_-]+$/u.test(value)) throw new Error(message);
  const decodedLength = Math.floor(value.length * 3 / 4);
  if (decodedLength > maximumBytes) throw new Error(limitMessage);
  if (options.exactBytes !== undefined && decodedLength !== options.exactBytes)
    throw new Error(message);
  const remainder = value.length % 4;
  const finalValue = base64UrlAlphabet.indexOf(value.at(-1));
  if ((remainder === 2 && (finalValue & 0x0f) !== 0) ||
      (remainder === 3 && (finalValue & 0x03) !== 0)) throw new Error(message);
  return decodedLength;
}

function fromBase64Url(value, message, options = {}) {
  const decodedLength = base64UrlDecodedLength(value, message, options);
  const normalized = value.replaceAll("-", "+").replaceAll("_", "/");
  const padded = normalized + "=".repeat((4 - normalized.length % 4) % 4);
  let binary;
  try { binary = atob(padded); }
  catch { throw new Error(message); }
  if (binary.length !== decodedLength) throw new Error(message);
  const bytes = new Uint8Array(decodedLength);
  for (let index = 0; index < decodedLength; index++) bytes[index] = binary.charCodeAt(index);
  return bytes;
}

function bytesOf(value) {
  if (value instanceof Uint8Array) return value;
  if (value instanceof ArrayBuffer) return new Uint8Array(value);
  if (ArrayBuffer.isView(value)) return new Uint8Array(value.buffer, value.byteOffset, value.byteLength);
  throw new Error("Select a valid Pirdy encrypted backup file.");
}

function parseEnvelope(fileBytes, limits = defaultLimits) {
  const bytes = bytesOf(fileBytes);
  if (bytes.byteLength === 0 || bytes.byteLength > limits.maximumEncryptedFileBytes)
    throw new Error("The encrypted Pirdy backup exceeds the browser safety limit.");
  let envelope;
  try { envelope = JSON.parse(decoder.decode(bytes)); }
  catch { throw new Error("The selected file is not a valid encrypted Pirdy workspace backup."); }
  if (!exactKeys(envelope, ["format", "algorithm", "nonce", "ciphertext"]) ||
      envelope.format !== format || envelope.algorithm !== algorithm ||
      typeof envelope.nonce !== "string" || typeof envelope.ciphertext !== "string")
    throw new Error("The selected file uses an unsupported Pirdy backup format.");
  base64UrlDecodedLength(envelope.nonce, "The Pirdy backup nonce is invalid.", {
    maximumBytes: 12, exactBytes: 12,
  });
  const ciphertextLength = base64UrlDecodedLength(envelope.ciphertext,
    "The encrypted Pirdy backup payload is invalid.", {
      maximumBytes: limits.maximumCiphertextBytes,
      limitMessage: "The encrypted Pirdy backup payload exceeds the browser safety limit.",
    });
  if (ciphertextLength < 17) throw new Error("The encrypted Pirdy backup payload is invalid.");
  return envelope;
}

function validTimestamp(value) {
  if (typeof value !== "string") return false;
  const parsed = new Date(value);
  return !Number.isNaN(parsed.valueOf()) && parsed.toISOString() === value;
}

function forbiddenBackupField(value) {
  if (Array.isArray(value)) return value.some(forbiddenBackupField);
  if (!plainObject(value)) return false;
  return Object.entries(value).some(([key, nested]) =>
    (/^(?:accessToken|refreshToken|idToken|password|claimPin|uploadUrl|downloadUrl|sasToken|secret)$/iu.test(key) &&
      nested !== null && nested !== undefined && String(nested) !== "") || forbiddenBackupField(nested));
}

function validateAttachmentShape(attachments, limits = defaultLimits) {
  if (attachments === undefined) return;
  if (!Array.isArray(attachments)) throw new Error("The Pirdy backup attachment list is invalid.");
  if (attachments.length > limits.maximumAttachmentCount)
    throw new Error("The Pirdy backup attachment count exceeds the browser safety limit.");
  const identities = new Set();
  let totalBytes = 0;
  for (const attachment of attachments) {
    if (!plainObject(attachment)) throw new Error("The Pirdy backup contains an invalid attachment.");
    const kind = typeof attachment.kind === "string" ? attachment.kind.trim() : "";
    const id = typeof attachment.id === "string" ? attachment.id.trim() : "";
    if (!kind || kind.length > 64 || !id || id.length > 128 ||
        typeof attachment.bytes !== "string" || !/^[A-Fa-f0-9]{64}$/u.test(String(attachment.sha256 ?? "")))
      throw new Error("The Pirdy backup contains an invalid attachment.");
    const byteLength = base64UrlDecodedLength(attachment.bytes,
      "The Pirdy backup contains invalid attachment bytes.", {
        maximumBytes: limits.maximumAttachmentBytes,
        limitMessage: "A Pirdy backup attachment exceeds the browser safety limit.",
      });
    if (attachment.byteLength !== undefined &&
        (!Number.isSafeInteger(attachment.byteLength) || attachment.byteLength < 0 || attachment.byteLength !== byteLength))
      throw new Error("The Pirdy backup attachment size is invalid.");
    totalBytes += byteLength;
    if (totalBytes > limits.maximumDecodedAttachmentBytes)
      throw new Error("The Pirdy backup attachment total exceeds the browser safety limit.");
    const identity = `${kind}\u0000${id}`;
    if (identities.has(identity)) throw new Error("The Pirdy backup contains duplicate attachments.");
    identities.add(identity);
  }
}

function validateBundleShape(bundle, expectedAccountId = null, limits = defaultLimits) {
  if (!plainObject(bundle) || !plainObject(bundle.workspace))
    throw new Error("The decrypted Pirdy backup content is invalid.");
  const owner = portableAccountId(bundle.accountId,
    "A verified portable Pirdy account is required for an encrypted backup.");
  if (bundle.workspace.accountId != null && portableAccountId(bundle.workspace.accountId) !== owner)
    throw new Error("The decrypted Pirdy backup account scope is invalid.");
  if (expectedAccountId !== null && owner !== expectedAccountId)
    throw customerSafeBackupError("This backup belongs to a different Pirdy account. No data was restored.");
  validateAttachmentShape(bundle.attachments, limits);
  if (forbiddenBackupField(bundle))
    throw new Error("The Pirdy backup contains temporary credentials or download links and was rejected.");
  return owner;
}

function serializableBundle(bundle, limits) {
  // Bound attacker-controlled base64 fields before JSON.stringify duplicates
  // them into a second large string. Cycles elsewhere remain handled below.
  if (plainObject(bundle)) validateAttachmentShape(bundle.attachments, limits);
  let serialized;
  try { serialized = JSON.stringify(bundle); }
  catch { throw new Error("The Pirdy workspace cannot be safely serialized for backup."); }
  if (!serialized) throw new Error("The Pirdy workspace cannot be safely serialized for backup.");
  if (serialized.length > limits.maximumPlaintextBytes)
    throw new Error("The Pirdy workspace exceeds the browser backup safety limit.");
  let clone;
  try { clone = JSON.parse(serialized); }
  catch { throw new Error("The Pirdy workspace cannot be safely serialized for backup."); }
  validateBundleShape(clone, null, limits);
  return clone;
}

async function verifyAttachments(bundle, crypto, limits) {
  for (const attachment of bundle.attachments ?? []) {
    const bytes = fromBase64Url(attachment.bytes, "The Pirdy backup contains invalid attachment bytes.", {
      maximumBytes: limits.maximumAttachmentBytes,
      limitMessage: "A Pirdy backup attachment exceeds the browser safety limit.",
    });
    try {
      const digest = new Uint8Array(await crypto.subtle.digest("SHA-256", bytes));
      const actual = [...digest].map(value => value.toString(16).padStart(2, "0")).join("").toUpperCase();
      if (actual !== attachment.sha256.toUpperCase())
        throw new Error("A Pirdy backup attachment failed SHA-256 verification.");
    } finally { bytes.fill(0); }
  }
}

// The recovery key is generated in the browser, returned separately, and never
// included in the backup envelope or durable browser workspace record. Callers
// may include verified source/prepared bytes in bundle.attachments; SAS URLs and
// other temporary credentials are rejected before encryption.
export async function createEncryptedWorkspaceBackup(bundle, options = {}) {
  const crypto = cryptoApi(options.crypto);
  const limits = operationLimits(options.limits);
  const safeBundle = serializableBundle(bundle, limits);
  const owner = validateBundleShape(safeBundle, null, limits);
  await verifyAttachments(safeBundle, crypto, limits);
  const now = options.now ?? (() => new Date());
  const exportedAt = now();
  if (!(exportedAt instanceof Date) || Number.isNaN(exportedAt.valueOf()))
    throw new Error("The backup timestamp is invalid.");

  const rawKey = new Uint8Array(32);
  let nonce;
  let plain;
  let ciphertext;
  try {
    crypto.getRandomValues(rawKey);
    nonce = new Uint8Array(12);
    crypto.getRandomValues(nonce);
    const key = await crypto.subtle.importKey("raw", rawKey, { name: "AES-GCM" }, false, ["encrypt"]);
    const payload = JSON.stringify({
      formatVersion: 1, accountId: owner, exportedAtUtc: exportedAt.toISOString(), bundle: safeBundle,
    });
    if (payload.length > limits.maximumPlaintextBytes)
      throw new Error("The Pirdy workspace exceeds the browser backup safety limit.");
    plain = encoder.encode(payload);
    if (plain.byteLength > limits.maximumPlaintextBytes ||
        plain.byteLength + 16 > limits.maximumCiphertextBytes)
      throw new Error("The Pirdy workspace exceeds the browser backup safety limit.");
    ciphertext = new Uint8Array(await crypto.subtle.encrypt({ name: "AES-GCM", iv: nonce,
      additionalData: encoder.encode(format), tagLength: 128 }, key, plain));
    const envelope = { format, algorithm, nonce: base64Url(nonce), ciphertext: base64Url(ciphertext) };
    const day = exportedAt.toISOString().slice(0, 10);
    const bytes = encoder.encode(JSON.stringify(envelope));
    if (bytes.byteLength > limits.maximumEncryptedFileBytes) {
      bytes.fill(0);
      throw new Error("The encrypted Pirdy backup exceeds the browser safety limit.");
    }
    return { fileName: `pirdy-workspace-${day}.pirdybackup`, mediaType, bytes,
      recoveryKey: base64Url(rawKey) };
  } finally {
    plain?.fill(0);
    ciphertext?.fill(0);
    nonce?.fill(0);
    rawKey.fill(0);
  }
}

export async function restoreEncryptedWorkspaceBackup(fileBytes, recoveryKey,
  expectedAccountId, options = {}) {
  const crypto = cryptoApi(options.crypto);
  const limits = operationLimits(options.limits);
  const expected = portableAccountId(expectedAccountId);
  if (typeof recoveryKey !== "string" || recoveryKey.length > 64)
    throw new Error("The Pirdy backup recovery key is invalid.");
  const envelope = parseEnvelope(fileBytes, limits);
  const rawKey = fromBase64Url(recoveryKey.trim(), "The Pirdy backup recovery key is invalid.", {
    maximumBytes: 32, exactBytes: 32,
  });
  try {
    const nonce = fromBase64Url(envelope.nonce, "The Pirdy backup nonce is invalid.", {
      maximumBytes: 12, exactBytes: 12,
    });
    const ciphertext = fromBase64Url(envelope.ciphertext,
      "The encrypted Pirdy backup payload is invalid.", {
        maximumBytes: limits.maximumCiphertextBytes,
        limitMessage: "The encrypted Pirdy backup payload exceeds the browser safety limit.",
      });
    let plain, key;
    try {
      key = await crypto.subtle.importKey("raw", rawKey, { name: "AES-GCM" }, false, ["decrypt"]);
      try {
        plain = new Uint8Array(await crypto.subtle.decrypt({ name: "AES-GCM", iv: nonce,
          additionalData: encoder.encode(format), tagLength: 128 }, key, ciphertext));
      } catch { throw customerSafeBackupError("The backup could not be decrypted. Check the recovery key and file."); }
      if (plain.byteLength > limits.maximumPlaintextBytes)
        throw new Error("The decrypted Pirdy backup content exceeds the browser safety limit.");
      let restored;
      try { restored = JSON.parse(decoder.decode(plain)); }
      catch { throw new Error("The decrypted Pirdy backup content is invalid."); }
      if (!exactKeys(restored, ["formatVersion", "accountId", "exportedAtUtc", "bundle"]) ||
          restored.formatVersion !== 1 || !validTimestamp(restored.exportedAtUtc) || !plainObject(restored.bundle))
        throw new Error("The decrypted Pirdy backup content is invalid.");
      const declared = portableAccountId(restored.accountId,
        "The decrypted Pirdy backup account scope is invalid.");
      if (declared !== expected)
        throw customerSafeBackupError("This backup belongs to a different Pirdy account. No data was restored.");
      if (validateBundleShape(restored.bundle, expected, limits) !== declared)
        throw new Error("The decrypted Pirdy backup account scope is invalid.");
      await verifyAttachments(restored.bundle, crypto, limits);
      return restored;
    } finally {
      plain?.fill(0);
      nonce.fill(0);
      ciphertext.fill(0);
    }
  } finally { rawKey.fill(0); }
}

function localWorkspaceSummary(record) {
  return {
    accountId: record.accountId,
    exportedAtUtc: record.exportedAtUtc,
    restoredAtUtc: record.restoredAtUtc,
  };
}

function validateStoredRecord(record, expectedAccountId) {
  if (!plainObject(record) || record.recordVersion !== localWorkspaceRecordVersion ||
      record.accountId !== expectedAccountId || !validTimestamp(record.exportedAtUtc) ||
      !validTimestamp(record.restoredAtUtc))
    throw new Error("The encrypted browser-local Pirdy workspace record is invalid. It was not changed.");
  parseEnvelope(record.encryptedBytes);
  return record;
}

export class AccountScopedLocalWorkspaceStore {
  constructor(driver) {
    if (!driver || typeof driver.read !== "function" || typeof driver.replace !== "function")
      throw new TypeError("An atomic local-workspace storage driver is required.");
    this.driver = driver;
    this.queues = new Map();
  }

  async load(accountId) {
    const owner = portableAccountId(accountId);
    const queued = this.queues.get(owner);
    if (queued) await queued.catch(() => {});
    const record = await this.driver.read(owner);
    return record == null ? null : localWorkspaceSummary(validateStoredRecord(record, owner));
  }

  async replace(accountId, fileBytes, restored, options = {}) {
    const owner = portableAccountId(accountId);
    if (!exactKeys(restored, ["formatVersion", "accountId", "exportedAtUtc", "bundle"]) ||
        restored.formatVersion !== 1 || !validTimestamp(restored.exportedAtUtc) ||
        portableAccountId(restored.accountId) !== owner || validateBundleShape(restored.bundle, owner) !== owner)
      throw new Error("The decrypted Pirdy backup account scope is invalid. The local workspace was not changed.");
    // Validate the caller's view before copying so even the durable-store path
    // cannot allocate an unbounded second buffer.
    parseEnvelope(fileBytes);
    const encryptedBytes = bytesOf(fileBytes).slice();
    const restoredAt = (options.now ?? (() => new Date()))();
    if (!(restoredAt instanceof Date) || Number.isNaN(restoredAt.valueOf()))
      throw new Error("The restore timestamp is invalid. The local workspace was not changed.");
    const record = {
      recordVersion: localWorkspaceRecordVersion,
      accountId: owner,
      exportedAtUtc: restored.exportedAtUtc,
      restoredAtUtc: restoredAt.toISOString(),
      encryptedBytes: encryptedBytes.buffer,
    };
    const previous = this.queues.get(owner) ?? Promise.resolve();
    const pending = previous.catch(() => {}).then(async () => {
      try { await this.driver.replace(record); }
      catch { throw customerSafeBackupError("The browser could not atomically save the restored Pirdy workspace. The existing local workspace is unchanged."); }
      return localWorkspaceSummary(record);
    });
    this.queues.set(owner, pending);
    try { return await pending; }
    finally { if (this.queues.get(owner) === pending) this.queues.delete(owner); }
  }
}

class IndexedDbLocalWorkspaceDriver {
  constructor(indexedDb) { this.indexedDb = indexedDb; this.database = null; }

  async open() {
    if (this.database) return this.database;
    if (!this.indexedDb || typeof this.indexedDb.open !== "function")
      throw new Error("Encrypted browser-local workspace storage is unavailable.");
    this.database = await new Promise((resolve, reject) => {
      const request = this.indexedDb.open(localWorkspaceDatabaseName, 1);
      request.onupgradeneeded = () => {
        if (!request.result.objectStoreNames.contains(localWorkspaceStoreName))
          request.result.createObjectStore(localWorkspaceStoreName, { keyPath: "accountId" });
      };
      request.onsuccess = () => {
        request.result.onversionchange = () => request.result.close();
        resolve(request.result);
      };
      request.onerror = () => reject(request.error);
      request.onblocked = () => reject(new Error("Encrypted browser-local workspace storage is busy in another tab."));
    });
    return this.database;
  }

  async read(accountId) {
    const database = await this.open();
    return new Promise((resolve, reject) => {
      const transaction = database.transaction(localWorkspaceStoreName, "readonly");
      const request = transaction.objectStore(localWorkspaceStoreName).get(accountId);
      let result;
      request.onsuccess = () => { result = request.result ?? null; };
      request.onerror = () => reject(request.error);
      transaction.oncomplete = () => resolve(result ?? null);
      transaction.onerror = () => reject(transaction.error);
      transaction.onabort = () => reject(transaction.error ?? new Error("The local workspace read was cancelled."));
    });
  }

  async replace(record) {
    const database = await this.open();
    await new Promise((resolve, reject) => {
      // A single read/write transaction atomically replaces exactly one account
      // partition. Any request, quota, abort, or commit failure retains the old record.
      const transaction = database.transaction(localWorkspaceStoreName, "readwrite");
      transaction.objectStore(localWorkspaceStoreName).put(record);
      transaction.oncomplete = () => resolve();
      transaction.onerror = () => reject(transaction.error);
      transaction.onabort = () => reject(transaction.error ?? new Error("The local workspace restore was cancelled."));
    });
  }
}

export function createBrowserLocalWorkspaceStore(indexedDb = globalThis.indexedDB) {
  return new AccountScopedLocalWorkspaceStore(new IndexedDbLocalWorkspaceDriver(indexedDb));
}

export function encodeBackupAttachmentBytes(value) {
  const bytes = bytesOf(value);
  if (bytes.byteLength === 0 || bytes.byteLength > defaultLimits.maximumAttachmentBytes)
    throw new Error("A Pirdy backup attachment exceeds the browser safety limit.");
  return base64Url(bytes);
}

export function downloadEncryptedWorkspaceBackup(result, options = {}) {
  const documentApi = options.document ?? globalThis.document;
  const urlApi = options.URL ?? globalThis.URL;
  const BlobType = options.Blob ?? globalThis.Blob;
  if (!result || result.mediaType !== mediaType || typeof result.fileName !== "string" ||
      !result.fileName.endsWith(".pirdybackup")) throw new Error("The encrypted Pirdy backup download is invalid.");
  const bytes = bytesOf(result.bytes);
  if (bytes.byteLength === 0 || bytes.byteLength > defaultLimits.maximumEncryptedFileBytes)
    throw new Error("The encrypted Pirdy backup exceeds the browser safety limit.");
  if (!documentApi?.createElement || !documentApi?.body || !urlApi?.createObjectURL || !BlobType)
    throw new Error("This browser cannot start the encrypted backup download.");
  const url = urlApi.createObjectURL(new BlobType([bytes], { type: mediaType }));
  try {
    const anchor = documentApi.createElement("a");
    anchor.href = url;
    anchor.download = result.fileName;
    anchor.rel = "noopener";
    anchor.hidden = true;
    documentApi.body.append(anchor);
    anchor.click();
    anchor.remove();
  } finally {
    (options.setTimeout ?? globalThis.setTimeout)(() => urlApi.revokeObjectURL(url), 0);
  }
}

export const encryptedWorkspaceBackupContract = Object.freeze({
  format, algorithm, mediaType, localWorkspaceDatabaseName, localWorkspaceStoreName,
  localWorkspaceRecordVersion, limits: defaultLimits,
});
